import { ParseResult } from 'papaparse';
import { splitEvery } from 'ramda';
import * as yup from 'yup';

import { getVenueInventory } from 'apis/inventory-management-service/api';
import { ItemInventory } from 'apis/inventory-management-service/types';
import { getItemConfigurations, searchItemsInPickingAreas } from 'apis/picker-api/api';
import { ItemConfiguration } from 'apis/picker-api/types';
import { DepositPermutation } from 'apis/restaurant-api/merchant-admin/deposits/types';
import { getMenuItemsCommissions, getProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/api';
import {
  BulkChangeCategoryPayload,
  Category,
  Item,
  ItemCommission,
  ItemUpdatePayload,
  ProductHierarchyLevel,
} from 'apis/restaurant-api/merchant-admin/menu2/types';

import {
  ItemInventoryPayload,
  ItemCommissionsPayload,
  PickingAreasPayload,
  CategoryOrderPayload,
  getItemUpdatePayload,
  getInventoryUpdatePayload,
  getItemConfigurationPayload,
  getComissionsPayload,
  getPickingAreasPayload,
  getCategoriesPayload,
  getCategoryOrderPayload,
} from './payloadFactory';
import { csvColumnsTranslationMap, identicationColumns } from '../../constants';
import { CSVColumnKey, PickingAreasItemSearchResult } from '../../types';
import { flattenPickingAreasSearchResult } from '../../utils';
import { CsvValidationContext } from '../validationUtils';
import { csvValidationSchema, ValidCSVData } from '../validations';

export type PayloadsTuple = [
  ItemUpdatePayload[],
  ItemInventoryPayload[],
  ItemConfiguration[],
  ItemCommissionsPayload[],
  PickingAreasPayload[],
  BulkChangeCategoryPayload[],
  CategoryOrderPayload[],
];

const STARTING_ROW = 2;
const PICKING_AREA_SEARCH_CHUNK_SIZE = 1000;

const findDuplicateIdentifierRows = (
  csvData: Partial<Record<CSVColumnKey, string | undefined>>[],
  fields: CSVColumnKey[],
) => {
  if (fields.find(f => f === 'item_id')) {
    return csvData.reduce(
      (acc, curr, index) =>
        csvData.filter(row => row.item_id === curr.item_id).length > 1 ? [...acc, index + STARTING_ROW] : acc,
      [],
    );
  }
  return csvData.reduce(
    (acc, curr, index) =>
      csvData.filter(row => row.gtin === curr.gtin && row.merchant_sku === curr.merchant_sku).length > 1
        ? [...acc, index + STARTING_ROW]
        : acc,
    [],
  );
};

const findItemsForRow = (row: ValidCSVData, items: Item[], selectedFields: CSVColumnKey[]) => {
  const { item_id, gtin, merchant_sku } = row;
  if (item_id) {
    return items.filter(item => item.id === item_id);
  }
  if (gtin && !merchant_sku) {
    return items.filter(item => item.barcode_gtin === gtin);
  }
  if (merchant_sku && !gtin) {
    return items.filter(item => item.merchant_sku === merchant_sku);
  }
  if (gtin && merchant_sku) {
    if (!selectedFields.includes('gtin') && selectedFields.includes('merchant_sku')) {
      return items.filter(item => item.barcode_gtin === gtin);
    }
    if (!selectedFields.includes('merchant_sku') && selectedFields.includes('gtin')) {
      return items.filter(item => item.merchant_sku === merchant_sku);
    }
    return items.filter(item => item.barcode_gtin === gtin && item.merchant_sku === merchant_sku);
  }
  return [];
};

export class CsvFileError extends Error {
  id: string;
  values?: Record<string, string>;
  constructor({ id, values }: { id: string; values?: Record<string, string> }) {
    super(id);
    this.id = id;
    this.values = values;
    this.message = id;
    this.name = 'CsvFileError';
  }
}

export type CsvValidationSettings = {
  overTheCounter: boolean;
};

const isValidCSVColumnKey = (column: string): column is CSVColumnKey =>
  Object.keys(csvColumnsTranslationMap).includes(column);

export const asyncValidateCsvResults = (
  results: ParseResult<Partial<Record<CSVColumnKey, string | undefined>>>,
  context: CsvValidationContext,
  settings: CsvValidationSettings,
) =>
  new Promise<{ validData: ValidCSVData[]; fields: CSVColumnKey[] }>((resolve, reject) => {
    try {
      const validCsvColumns = (results.meta.fields || []).filter(isValidCSVColumnKey);
      const clearRawData = results.data.map(row =>
        validCsvColumns.reduce((acc, curr) => ({ ...acc, [curr]: row[curr] }), {}),
      );

      if (!validCsvColumns.length || clearRawData.length === 0) {
        throw new CsvFileError({ id: 'menu.csvItemPriceUpdate.importErrors.emptyFile' });
      }
      if (!identicationColumns.some(idCol => validCsvColumns.includes(idCol))) {
        throw new CsvFileError({ id: 'menu.csvItemUpdate.itemIdentifierMissing' });
      }
      if (!settings.overTheCounter && validCsvColumns.includes('is_over_the_counter')) {
        throw new CsvFileError({ id: 'menu.csvItemUpdate.overTheCounterForbidden' });
      }

      const validCsvData = yup
        .array()
        .of(csvValidationSchema.pick(validCsvColumns))
        .required()
        .validateSync(clearRawData, { abortEarly: false, context });

      const duplicateIdentifierRows = findDuplicateIdentifierRows(clearRawData, validCsvColumns);
      if (duplicateIdentifierRows.length > 0) {
        const identifier = duplicateIdentifierRows.join(', ');
        throw new CsvFileError({ id: 'menu.csvItemUpdate.duplicateItem', values: { identifier } });
      }

      resolve({ validData: validCsvData, fields: validCsvColumns });
    } catch (error) {
      reject(error);
    }
  });

export const mapValidationErrors = (
  error: yup.ValidationError,
): { row?: number; column?: string; message: { id: string; values?: Record<string, any> } } => {
  const [pathRow, column]: (string | undefined)[] = (error.path || '').split('.');
  const row = pathRow ? Number(pathRow.replace(/[^\d]/g, '')) + STARTING_ROW : undefined;
  const message = error.message as unknown as { id: string; values?: Record<string, any> };
  return { row, column, message };
};

export const getCsvSettingsData = async (
  menuId: string,
  venueId: string,
  items: Item[],
  selectedFields: CSVColumnKey[],
  customCommissionRead: boolean,
) => {
  const shouldFetchInventory =
    selectedFields.includes('use_inventory_management') ||
    selectedFields.includes('use_limited_quantity') ||
    selectedFields.includes('item_balance');
  const shouldFetchHierarchies = selectedFields.includes('product_hierarchy_id');
  const shouldFetchItemConfigs = selectedFields.includes('location');
  const shouldFetchCommissions =
    selectedFields.includes('commission_delivery') || selectedFields.includes('commission_pickup');
  const shouldFetchPickingAreas =
    selectedFields.includes('picking_area') || selectedFields.includes('picking_sequence');

  const inventory = shouldFetchInventory ? await getVenueInventory(venueId) : [];
  const { product_hierarchy_levels: productHierarchyLevels } = shouldFetchHierarchies
    ? await getProductInformationSettings(menuId)
    : { product_hierarchy_levels: [] };
  const itemConfigs = shouldFetchItemConfigs ? await getItemConfigurations(menuId) : [];
  const itemCommissions = shouldFetchCommissions && customCommissionRead ? await getMenuItemsCommissions(menuId) : [];

  if (!shouldFetchPickingAreas) {
    return { productHierarchyLevels, inventory, itemConfigs, itemCommissions, pickingAreasItemSearchResult: [] };
  }

  const pickingAreasItemSearchResult: PickingAreasItemSearchResult[] = [];
  const itemIdChunks = splitEvery(
    PICKING_AREA_SEARCH_CHUNK_SIZE,
    items.map(item => item.id),
  );
  for (const chunk of itemIdChunks) {
    const searchResult = await searchItemsInPickingAreas(venueId, { items: chunk });
    pickingAreasItemSearchResult.push(...flattenPickingAreasSearchResult(searchResult));
  }

  return { productHierarchyLevels, inventory, itemConfigs, itemCommissions, pickingAreasItemSearchResult };
};

export const getUpdatePayloadsFromCsvData = (
  data: ValidCSVData[],
  items: Item[],
  settings: {
    selectedFields: CSVColumnKey[];
    categories: Category[];
    language: Language;
    productHierarchyLevels: ProductHierarchyLevel[];
    inventory: ItemInventory[];
    itemConfigs: ItemConfiguration[];
    itemCommissions: ItemCommission[];
    pickingAreasItemSearchResult: PickingAreasItemSearchResult[];
    deposits?: DepositPermutation[];
    menuId: string;
  },
): { payloads: PayloadsTuple; unmatchedData: ValidCSVData[] } => {
  const {
    selectedFields,
    categories,
    language,
    productHierarchyLevels,
    inventory,
    itemConfigs,
    itemCommissions,
    pickingAreasItemSearchResult,
    menuId,
    deposits,
  } = settings;
  const originalItemsPerRow = data.map((row, index) => {
    const retrievedItems = findItemsForRow(row, items, selectedFields);
    return { row, rowNumber: STARTING_ROW + index, items: retrievedItems };
  });
  const unmatchedData = data.filter(row => findItemsForRow(row, items, selectedFields).length === 0);
  const payloads = originalItemsPerRow.reduce<PayloadsTuple>(
    (acc, currSet) => {
      for (const originalItem of currSet.items) {
        const itemPayload = getItemUpdatePayload(currSet.row, originalItem, {
          selectedFields,
          language,
          deposits,
          productHierarchyLevels,
        });
        if (itemPayload) {
          acc[0].push(itemPayload);
        }

        const itemInventoryPayload = getInventoryUpdatePayload(currSet.row, originalItem, {
          selectedFields,
          inventory,
        });
        if (itemInventoryPayload) {
          acc[1].push(itemInventoryPayload);
        }

        const itemConfigPayload = getItemConfigurationPayload(currSet.row, originalItem, {
          selectedFields,
          itemConfigs,
          menuId,
        });
        if (itemConfigPayload) {
          acc[2].push(itemConfigPayload);
        }

        const commissionsPayload = getComissionsPayload(currSet.row, originalItem, { selectedFields, itemCommissions });
        if (commissionsPayload) {
          acc[3].push(commissionsPayload);
        }

        const pickingAreasPayload = getPickingAreasPayload(currSet.row, originalItem, {
          selectedFields,
          pickingAreasItemSearchResult,
        });
        if (pickingAreasPayload) {
          acc[4].push(pickingAreasPayload);
        }

        const categoryChangePayload = getCategoriesPayload(currSet.row, originalItem, { selectedFields, categories });
        if (categoryChangePayload) {
          acc[5].push(categoryChangePayload);
        }

        const categoryOrderPayload = getCategoryOrderPayload(currSet.row, originalItem, { selectedFields, categories });
        if (categoryOrderPayload) {
          acc[6].push(categoryOrderPayload);
        }
      }
      return acc;
    },
    [[], [], [], [], [], [], []],
  );
  return { payloads, unmatchedData };
};
